import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash, PaperPlaneTilt, FloppyDisk, FolderOpen, BookmarkSimple, Eye } from '@phosphor-icons/react';

export default function BroadcastPage() {
  const { selectedBot, bots } = useOutletContext();
  const [text1, setText1] = useState('');
  const [text2, setText2] = useState('');
  const [buttons, setButtons] = useState([]);
  const [mediaType, setMediaType] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [requestMobile, setRequestMobile] = useState(false);
  const [showBtnLabel, setShowBtnLabel] = useState(true);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [broadcastProgress, setBroadcastProgress] = useState(null);
  const progressRef = useRef(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [defaultBroadcasts, setDefaultBroadcasts] = useState([]);

  const bot = bots.find(b => b.id === selectedBot);

  useEffect(() => {
    API.get('/broadcast/templates').then(r => setTemplates(r.data.templates || [])).catch(() => {});
  }, []);

  // Load quick replies as default broadcast messages from bot settings
  useEffect(() => {
    if (selectedBot) {
      API.get(`/bots/${selectedBot}/settings`).then(r => {
        setQuickReplies(r.data.settings?.quick_replies || []);
      }).catch(() => {});
    }
  }, [selectedBot]);

  if (!selectedBot || !bot) {
    return (
      <div className="space-y-4" data-testid="broadcast-page">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Broadcast</h1>
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">
          Select a bot from the sidebar to send broadcasts
        </div>
      </div>
    );
  }

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('bot_id', selectedBot);
      if (text1) formData.append('text_1', text1);
      if (text2) formData.append('text_2', text2);
      formData.append('inline_buttons', JSON.stringify(buttons));
      formData.append('request_mobile', requestMobile.toString());
      formData.append('show_button_label', showBtnLabel.toString());
      if (mediaType) formData.append('media_type', mediaType);
      if (mediaFile) formData.append('media_file', mediaFile);
      if (mediaType === 'poll') {
        formData.append('poll_question', pollQuestion);
        formData.append('poll_options', JSON.stringify(pollOptions.filter(o => o.trim())));
      }
      const { data } = await API.post('/broadcast/send', formData);
      toast.success(data.message || 'Broadcast started');
      // Start progress tracking
      if (data.broadcast?.id) {
        setBroadcastProgress({ id: data.broadcast.id, total: data.broadcast.total_users, sent: 0, success: 0, fail: 0, status: 'in_progress' });
        progressRef.current = setInterval(async () => {
          try {
            const { data: p } = await API.get(`/broadcast/progress/${data.broadcast.id}`);
            setBroadcastProgress(prev => ({ ...prev, ...p }));
            if (p.status === 'completed') {
              clearInterval(progressRef.current);
              toast.success(`Broadcast complete: ${p.success} success, ${p.fail} failed`);
            }
          } catch { clearInterval(progressRef.current); }
        }, 1500);
      }
      setText1(''); setText2(''); setButtons([]); setMediaType(''); setMediaFile(null);
      setPollQuestion(''); setPollOptions(['', '']); setRequestMobile(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) return toast.error('Enter a template name');
    try {
      await API.post('/broadcast/templates', {
        name: templateName, text_1: text1, text_2: text2,
        inline_buttons: buttons, media_type: mediaType || null, request_mobile: requestMobile,
      });
      toast.success('Template saved');
      setSaveDialogOpen(false);
      setTemplateName('');
      API.get('/broadcast/templates').then(r => setTemplates(r.data.templates || []));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const loadTemplate = (t) => {
    setText1(t.text_1 || ''); setText2(t.text_2 || '');
    setButtons(t.inline_buttons || []); setMediaType(t.media_type || '');
    setRequestMobile(t.request_mobile || false);
    setLoadDialogOpen(false);
    toast.success(`Template "${t.name}" loaded`);
  };

  const deleteTemplate = async (id) => {
    try {
      await API.delete(`/broadcast/templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="broadcast-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Broadcast</h1>
          <p className="text-sm text-muted-foreground mt-1">Send to all users of {bot.bot_name}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="preview-broadcast-btn" variant="outline" size="sm" onClick={() => setPreviewOpen(true)} className="gap-1.5">
            <Eye size={16} /> Preview
          </Button>
          <Button data-testid="load-template-btn" variant="outline" size="sm" onClick={() => setLoadDialogOpen(true)} className="gap-1.5">
            <FolderOpen size={16} /> Templates
          </Button>
          <Button data-testid="save-template-btn" variant="outline" size="sm" onClick={() => setSaveDialogOpen(true)} className="gap-1.5">
            <BookmarkSimple size={16} /> Save
          </Button>
        </div>
      </div>

      <div className="rounded-lg p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
        <p className="text-sm">
          <span className="text-lg mr-1.5">📋</span>
          <strong className="text-blue-700 dark:text-blue-300">Broadcast Order:</strong>{' '}
          <span className="text-blue-600 dark:text-blue-400">
            🖼️ Image/GIF/Video → 📝 Text 1 → 📝 Text 2 + Inline Buttons
          </span>
        </p>
      </div>

      {/* Broadcast Progress Bar */}
      {broadcastProgress && (
        <div className="border border-border rounded-lg p-4 space-y-3" data-testid="broadcast-progress">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              {broadcastProgress.status === 'completed' ? '✅' : '📡'} Broadcast {broadcastProgress.status === 'completed' ? 'Complete' : 'In Progress'}
            </h4>
            <span className="text-xs font-mono text-muted-foreground">
              {broadcastProgress.sent || (broadcastProgress.success + broadcastProgress.fail)}/{broadcastProgress.total}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-green-500 to-emerald-500"
              style={{ width: `${broadcastProgress.total > 0 ? Math.min(100, ((broadcastProgress.sent || (broadcastProgress.success + broadcastProgress.fail)) / broadcastProgress.total) * 100) : 0}%` }}
            />
          </div>
          <div className="flex gap-4 text-xs">
            <span className="text-green-600 dark:text-green-400 font-mono">✓ {broadcastProgress.success} success</span>
            <span className="text-red-600 dark:text-red-400 font-mono">✗ {broadcastProgress.fail} failed</span>
          </div>
          {broadcastProgress.status === 'completed' && (
            <Button variant="ghost" size="sm" onClick={() => setBroadcastProgress(null)} className="text-xs">Dismiss</Button>
          )}
        </div>
      )}

      <form onSubmit={handleSend} className="space-y-6">
        <div className="border border-border rounded-md p-5 space-y-4">
          <h4 className="font-medium text-base">🖼️ Media (Optional)</h4>
          <div className="flex flex-wrap gap-1.5 mb-2">
            <button type="button" onClick={() => setMediaType('photo')} data-testid="quick-media-photo"
              className={`px-3 py-1.5 text-xs border rounded-lg transition-all ${mediaType === 'photo' ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border hover:bg-accent'}`}>
              📷 Photo
            </button>
            <button type="button" onClick={() => setMediaType('gif')} data-testid="quick-media-gif"
              className={`px-3 py-1.5 text-xs border rounded-lg transition-all ${mediaType === 'gif' ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border hover:bg-accent'}`}>
              🎞️ GIF
            </button>
            <button type="button" onClick={() => setMediaType('video')} data-testid="quick-media-video"
              className={`px-3 py-1.5 text-xs border rounded-lg transition-all ${mediaType === 'video' ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border hover:bg-accent'}`}>
              🎬 Video
            </button>
            <button type="button" onClick={() => setMediaType('poll')} data-testid="quick-media-poll"
              className={`px-3 py-1.5 text-xs border rounded-lg transition-all ${mediaType === 'poll' ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border hover:bg-accent'}`}>
              📊 Poll
            </button>
            {mediaType && (
              <button type="button" onClick={() => { setMediaType(''); setMediaFile(null); }} data-testid="clear-media-btn"
                className="px-3 py-1.5 text-xs border border-destructive/30 text-destructive rounded-lg hover:bg-destructive/10 transition-all">
                ✕ Clear
              </button>
            )}
          </div>
          {mediaType && mediaType !== 'poll' && (
            <div className="space-y-1.5">
              <Label>Upload File</Label>
              <Input data-testid="media-file-input" type="file" onChange={e => setMediaFile(e.target.files[0])} accept={mediaType === 'photo' ? 'image/*' : mediaType === 'gif' ? 'image/gif' : 'video/*'} />
            </div>
          )}
          {mediaType === 'poll' && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Poll Question</Label>
                <Input data-testid="poll-question" value={pollQuestion} onChange={e => setPollQuestion(e.target.value)} placeholder="Your question..." />
              </div>
              {pollOptions.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <Input data-testid={`poll-option-${i}`} value={opt} onChange={e => { const o = [...pollOptions]; o[i] = e.target.value; setPollOptions(o); }} placeholder={`Option ${i + 1}`} />
                  {i >= 2 && <Button type="button" variant="ghost" size="sm" onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}><Trash size={14} /></Button>}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setPollOptions([...pollOptions, ''])} className="gap-1"><Plus size={14} /> Add Option</Button>
            </div>
          )}
        </div>

        <div className="border border-border rounded-md p-5 space-y-4">
          <h4 className="font-medium text-base">📝 Messages</h4>
          <p className="text-xs text-muted-foreground">Sent as separate messages to each user</p>
          <div className="flex flex-wrap gap-1.5 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground self-center mr-1">Defaults:</span>
            <button type="button" onClick={() => setText1('Hello! Here is an important update for you.')} data-testid="default-text1-update"
              className="px-2 py-1 text-[11px] border border-border rounded-full hover:bg-accent transition-colors">📢 Update</button>
            <button type="button" onClick={() => setText1('Welcome to our community! We are glad to have you here.')} data-testid="default-text1-welcome"
              className="px-2 py-1 text-[11px] border border-border rounded-full hover:bg-accent transition-colors">👋 Welcome</button>
            <button type="button" onClick={() => setText1('Special offer just for you! Check out the details below.')} data-testid="default-text1-offer"
              className="px-2 py-1 text-[11px] border border-border rounded-full hover:bg-accent transition-colors">🎁 Offer</button>
            <button type="button" onClick={() => setText2('For more information, click the buttons below.')} data-testid="default-text2-info"
              className="px-2 py-1 text-[11px] border border-primary/30 bg-primary/5 text-primary rounded-full hover:bg-primary/10 transition-colors">ℹ️ Info T2</button>
          </div>
          <div className="space-y-1.5">
            <Label>Text 1 (separate message)</Label>
            <Textarea data-testid="broadcast-text-1" value={text1} onChange={e => setText1(e.target.value)} placeholder="First message line..." rows={3} />
          </div>
          <div className="space-y-1.5">
            <Label>Text 2 (separate message with inline buttons)</Label>
            <Textarea data-testid="broadcast-text-2" value={text2} onChange={e => setText2(e.target.value)} placeholder="Second message line..." rows={3} />
          </div>
        </div>

        <div className="border border-border rounded-md p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-base">🔗 Inline Buttons</h4>
            <div className="flex gap-1.5">
              <Button type="button" variant="ghost" size="sm" className="text-xs h-7 px-2" data-testid="preset-btn-website"
                onClick={() => setButtons([...buttons, { title: 'Visit Website', url: 'https://' }])}>+ Website</Button>
              <Button type="button" variant="ghost" size="sm" className="text-xs h-7 px-2" data-testid="preset-btn-channel"
                onClick={() => setButtons([...buttons, { title: 'Join Channel', url: 'https://t.me/' }])}>+ Channel</Button>
              <Button type="button" data-testid="broadcast-add-btn" variant="outline" size="sm" onClick={() => setButtons([...buttons, { title: '', url: '' }])} className="gap-1"><Plus size={14} /> Add</Button>
            </div>
          </div>
          {buttons.map((btn, i) => (
            <div key={i} className="flex gap-2">
              <Input data-testid={`bc-btn-title-${i}`} value={btn.title} onChange={e => { const b = [...buttons]; b[i].title = e.target.value; setButtons(b); }} placeholder="Title" />
              <Input data-testid={`bc-btn-url-${i}`} value={btn.url} onChange={e => { const b = [...buttons]; b[i].url = e.target.value; setButtons(b); }} placeholder="https://..." />
              <Button type="button" variant="ghost" size="sm" onClick={() => setButtons(buttons.filter((_, j) => j !== i))} className="text-destructive"><Trash size={16} /></Button>
            </div>
          ))}
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div>
              <Label>Show &ldquo;Please click below the Buttons :&rdquo; label</Label>
              <p className="text-xs text-muted-foreground mt-0.5">When ON, this text is sent above the inline buttons. When OFF, only buttons are sent.</p>
            </div>
            <Switch data-testid="broadcast-show-btn-label" checked={showBtnLabel} onCheckedChange={setShowBtnLabel} />
          </div>
        </div>

        <div className="border border-border rounded-md p-5">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-base">📱 Request Mobile Number</h4>
              <p className="text-xs text-muted-foreground mt-1">Include &ldquo;Share Mobile Number&rdquo; button</p>
            </div>
            <Switch data-testid="broadcast-request-mobile" checked={requestMobile} onCheckedChange={setRequestMobile} />
          </div>
        </div>

        <Button data-testid="send-broadcast-btn" type="submit" disabled={sending} className="w-full gap-2 active:scale-[0.98]">
          <PaperPlaneTilt size={20} /> {sending ? 'Sending...' : 'Send Broadcast'}
        </Button>
      </form>

      {/* PREVIEW DIALOG */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye size={20} /> Broadcast Preview</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground mb-3">This is how your broadcast will appear in Telegram</p>
          <div className="bg-[#0e1621] rounded-xl p-4 space-y-2 text-white text-sm" data-testid="broadcast-preview">
            {/* Media preview */}
            {mediaType && mediaType !== 'poll' && (
              <div className="bg-[#182533] rounded-lg p-3 flex items-center justify-center h-32 text-muted-foreground text-xs border border-white/10">
                {mediaType === 'photo' && '📷 Photo'}
                {mediaType === 'gif' && '🎞️ GIF'}
                {mediaType === 'video' && '🎬 Video'}
                {mediaFile ? ` - ${mediaFile.name}` : ' (no file selected)'}
              </div>
            )}
            {mediaType === 'poll' && (
              <div className="bg-[#182533] rounded-lg p-3 border border-white/10">
                <p className="font-semibold text-sm mb-2">📊 {pollQuestion || 'Poll Question'}</p>
                {pollOptions.filter(o => o.trim()).map((o, i) => (
                  <div key={i} className="bg-[#2b5278] rounded px-3 py-1.5 text-xs mb-1">{o}</div>
                ))}
              </div>
            )}
            {/* Text 1 - separate bubble */}
            {text1 && (
              <div className="bg-[#182533] rounded-lg px-3 py-2 border border-white/10 max-w-[85%] ml-auto">
                <p className="whitespace-pre-wrap text-sm">{text1}</p>
              </div>
            )}
            {/* Text 2 + inline buttons - separate bubble */}
            {(text2 || buttons.length > 0) && (
              <div className="max-w-[85%] ml-auto space-y-1">
                {text2 && (
                  <div className="bg-[#182533] rounded-lg px-3 py-2 border border-white/10">
                    <p className="whitespace-pre-wrap text-sm">{text2}</p>
                  </div>
                )}
                {!text2 && buttons.length > 0 && showBtnLabel && (
                  <div className="bg-[#182533] rounded-lg px-3 py-2 border border-white/10">
                    <p className="whitespace-pre-wrap text-sm">Please click below the Buttons :</p>
                  </div>
                )}
                {buttons.filter(b => b.title).map((btn, i) => (
                  <div key={i} className="bg-[#2b5278] text-[#6ab3f3] rounded-lg px-3 py-2 text-center text-xs font-medium cursor-pointer hover:bg-[#3a6a9a] transition-colors">
                    {btn.title} {btn.url && '↗'}
                  </div>
                ))}
              </div>
            )}
            {/* Mobile request */}
            {requestMobile && (
              <div className="bg-[#182533] rounded-lg p-2 border border-white/10">
                <p className="text-xs text-gray-400 mb-1.5">Please share your mobile number:</p>
                <div className="bg-[#2b5278] rounded px-3 py-2 text-center text-xs">📱 Share Mobile Number</div>
              </div>
            )}
            {!text1 && !text2 && !mediaType && buttons.length === 0 && !requestMobile && (
              <p className="text-center text-gray-500 py-4 text-xs">Empty broadcast — add content above</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* SAVE TEMPLATE */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BookmarkSimple size={20} /> Save as Template</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Template Name</Label>
              <Input data-testid="template-name-input" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="e.g., Weekly Newsletter" />
            </div>
            <div className="text-xs text-muted-foreground space-y-1 border border-border rounded p-3 bg-muted/30">
              <p><strong>Will save:</strong></p>
              {text1 && <p>Text 1: {text1.slice(0, 50)}{text1.length > 50 ? '...' : ''}</p>}
              {text2 && <p>Text 2: {text2.slice(0, 50)}{text2.length > 50 ? '...' : ''}</p>}
              {buttons.length > 0 && <p>{buttons.length} inline button(s)</p>}
              {mediaType && <p>Media: {mediaType}</p>}
              {requestMobile && <p>Mobile request: ON</p>}
            </div>
            <DialogFooter>
              <Button data-testid="template-save-confirm" onClick={saveTemplate} className="gap-1.5"><FloppyDisk size={16} /> Save</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* LOAD TEMPLATE */}
      <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderOpen size={20} /> Templates</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {templates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">No templates saved yet</p>
                <p className="text-xs mt-1">Compose a broadcast and click &ldquo;Save&rdquo;</p>
              </div>
            ) : templates.map(t => (
              <div key={t.id} className="border border-border rounded-md p-3 hover:bg-muted/30 transition-colors">
                <div className="flex items-center justify-between mb-1.5">
                  <h4 className="font-medium text-sm">{t.name}</h4>
                  <div className="flex gap-1">
                    <Button data-testid={`load-tpl-${t.id}`} variant="outline" size="sm" onClick={() => loadTemplate(t)} className="text-xs h-7 px-2">Load</Button>
                    <Button data-testid={`del-tpl-${t.id}`} variant="ghost" size="sm" onClick={() => deleteTemplate(t.id)} className="text-destructive h-7 px-2"><Trash size={14} /></Button>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {t.text_1 && <p className="truncate">T1: {t.text_1}</p>}
                  {t.text_2 && <p className="truncate">T2: {t.text_2}</p>}
                  <p className="text-[10px] font-mono mt-1">by {t.created_by_name}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
